/**
 * Tawk.to Chat Integration Utility
 * This utility provides functions to interact with the Tawk.to chat widget
 */

/**
 * Initialize Tawk.to chat widget
 */
export const initializeTawkTo = () => {
  try {
    console.log('Initializing Tawk.to chat widget...');

    // Check if already initialized
    if (window.Tawk_API) {
      console.log('Tawk.to already initialized');
      return;
    }

    // Initialize Tawk_API and Tawk_LoadStart
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Create and insert the Tawk.to script
    const script = document.createElement("script");
    script.async = true;
    script.src = 'https://embed.tawk.to/61cd412580b2296cfdd453f4/1fo4scv9o';
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    console.log('Tawk.to script injected successfully');
  } catch (error) {
    console.error('Error initializing Tawk.to:', error);
  }
};

/**
 * Opens the Tawk.to chat widget
 */
export const openTawkToChat = () => {
  // Check if Tawk.to API is available and loaded
  if (window.Tawk_API && typeof window.Tawk_API.toggle === 'function') {
    window.Tawk_API.toggle();
    return true;
  }
  
  // If maximize function is available, use it to open the chat
  if (window.Tawk_API && typeof window.Tawk_API.maximize === 'function') {
    window.Tawk_API.maximize();
    return true;
  }
  
  // Fallback: Try to find and click the Tawk.to widget button
  const tawkWidget = document.querySelector('[data-tawk]');
  if (tawkWidget) {
    tawkWidget.click();
    return true;
  }
  
  // Another fallback: Look for the Tawk.to iframe and try to trigger it
  const tawkFrame = document.querySelector('iframe[src*="tawk.to"]');
  if (tawkFrame && tawkFrame.parentElement) {
    const tawkButton = tawkFrame.parentElement.querySelector('[role="button"]');
    if (tawkButton) {
      tawkButton.click();
      return true;
    }
  }
  
  // Final fallback: Show message that chat is loading
  console.warn('Tawk.to chat widget not yet loaded');
  return false;
};

/**
 * Checks if Tawk.to is loaded and ready
 */
export const isTawkToReady = () => {
  return window.Tawk_API && (
    typeof window.Tawk_API.toggle === 'function' || 
    typeof window.Tawk_API.maximize === 'function'
  );
};

/**
 * Waits for Tawk.to to load and then executes callback
 */
export const waitForTawkTo = (callback, maxWaitTime = 5000) => {
  if (isTawkToReady()) {
    callback();
    return;
  }
  
  const startTime = Date.now();
  const checkInterval = setInterval(() => {
    if (isTawkToReady()) {
      clearInterval(checkInterval);
      callback();
    } else if (Date.now() - startTime > maxWaitTime) {
      clearInterval(checkInterval);
      console.warn('Tawk.to did not load within the expected time');
      // Still try to open chat as fallback
      callback();
    }
  }, 100);
};

/**
 * React hook to handle live chat functionality
 */
export const useLiveChat = () => {
  const handleLiveChatClick = (showAlert = true) => {
    const success = openTawkToChat();
    
    if (!success && showAlert) {
      alert('Live chat is loading. Please try again in a moment or contact us via email.');
    }
    
    return success;
  };
  
  const handleLiveChatClickAsync = () => {
    return new Promise((resolve) => {
      waitForTawkTo(() => {
        const success = openTawkToChat();
        resolve(success);
      });
    });
  };
  
  return {
    handleLiveChatClick,
    handleLiveChatClickAsync,
    isChatReady: isTawkToReady()
  };
};
